import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useApiFormProviderJoinCode } from '../../api-new/state/auth'
import { Pill } from './pill'
import { CopyValue } from './copy-value'
import { ConfirmDialog } from './confirm-dialog'
import { formatDate, formatDateTime, plural, serverMessage } from './format'
import { FleetGrid, useGridList } from './fleet-grid'
import { TableAgClient } from '../table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../table-builder/table-columns'
import { Menu, MenuItem } from '../menu/menu'
import { IconDelete, IconLoginPin, IconReload, IconUsers } from '../icon/icon'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

// A code's state, from the row alone.
export const joinCodeState = (code, now = Date.now()) => {
  if (code.revokedAt) return { label: 'Revoked', tone: 'bad' }
  if (code.expiresAt && new Date(code.expiresAt).getTime() <= now) return { label: 'Expired', tone: 'warn' }
  if (code.maxUses && code.useCount >= code.maxUses) return { label: 'Used up', tone: 'warn' }
  return { label: 'Active', tone: 'ok' }
}

export const usesText = code => (code.maxUses ? `${code.useCount} of ${code.maxUses}` : String(code.useCount))

// What a code is called: its label, else when it was made.
const codeName = code => code.label || `Code created ${formatDate(code.createdAt)}`

const usesCell = row => (row.data ? usesText(row.data) : '')
const expiresCell = row => (row.value ? formatDate(row.value) : 'Never')
const statusCell = row => {
  if (!row.data) return ''
  const state = joinCodeState(row.data)
  return <Pill tone={state.tone}>{state.label}</Pill>
}

// The end of the chosen day, in the admin's own time zone.
const endOfDay = date => new Date(`${date}T23:59:59`).toISOString()

const emptyForm = { label: '', roleID: '', expires: '', maxUses: '' }

// The join codes of one linked organization whose policy is "code": create
// (label, role, expiry, max uses), the code shown once to copy, rotate,
// revoke, and who joined with each. Only a hash is stored, so a lost code is
// rotated, never shown again. canEdit false: the list only.
export const JoinCodesPanel = ({ providerID, organizationID, organizationName, canEdit = true }) => {
  const listApi = useApiFormProviderJoinCode(`?providerID=${providerID}&organizationID=${organizationID}`)
  const api = useApiFormProviderJoinCode()
  const [codes, setCodes] = useState(undefined)
  const [roles, setRoles] = useState([])
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [issued, setIssued] = useState(null) // { code, verb } shown once
  const [confirm, setConfirm] = useState(null) // { kind, code }
  const [busy, setBusy] = useState(false)
  const [confirmError, confirmFailure, setConfirmError] = useErrorLine()
  const [usesFor, setUsesFor] = useState(null)
  const [uses, setUses] = useState(undefined)
  const newButton = useRef(null)
  const tableRef = useRef()
  const idp = `sw-codes-${organizationID}`

  const load = async () => {
    try {
      setCodes((await listApi.get()) || [])
    } catch (exp) {
      setCodes(null)
    }
  }

  useEffect(() => {
    load()
    if (canEdit) api.get('roles').then(r => setRoles(r || [])).catch(() => setRoles([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerID, organizationID])

  const set = patch => setForm(f => ({ ...f, ...patch }))

  const openCreate = () => {
    setForm({ ...emptyForm, roleID: roles.length === 1 ? String(roles[0].id) : '' })
    setError(null)
    setIssued(null)
    setCreating(true)
  }

  const handleCreate = async () => {
    setSaving(true)
    setError(null)
    try {
      const created = await api.post({
        providerID: Number(providerID),
        organizationID: Number(organizationID),
        label: form.label.trim(),
        roleID: Number(form.roleID),
        expiresAt: form.expires ? endOfDay(form.expires) : null,
        maxUses: form.maxUses ? Number(form.maxUses) : null
      })
      setCreating(false)
      setIssued({ code: created.joinCode, verb: 'created' })
      await load()
    } catch (exp) {
      setError(serverMessage(exp, 'Could not create the code.'), exp)
    }
    setSaving(false)
  }

  const handleConfirm = async () => {
    const { kind, code } = confirm
    setBusy(true)
    setConfirmError(null)
    try {
      if (kind === 'rotate') {
        const next = await api.postArg(`/${code.id}/rotate`)
        setIssued({ code: next.joinCode, verb: 'rotated' })
      } else {
        await api.delete(code.id)
      }
      setConfirm(null)
      await load()
    } catch (exp) {
      setConfirmError(serverMessage(exp, kind === 'rotate' ? 'Could not rotate the code.' : 'Could not revoke the code.'), exp)
    }
    setBusy(false)
  }

  // Read through a ref: the grid's row menu may hold the handler from an
  // earlier render.
  const usesForRef = useRef(null)
  usesForRef.current = usesFor

  const toggleUses = async code => {
    if (usesForRef.current === code.id) { setUsesFor(null); return }
    usesForRef.current = code.id
    setUsesFor(code.id)
    setUses(undefined)
    try {
      setUses((await api.get(`${code.id}/uses`)) || [])
    } catch (exp) {
      setUses(null)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Escape' && creating) { e.stopPropagation(); setCreating(false) }
    else if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault()
  }

  // The grid's rows: each code with its name and status as plain values, so
  // the grid sorts, filters and exports them.
  const rows = useMemo(
    () => (Array.isArray(codes) ? codes.map(code => ({ ...code, name: codeName(code), status: joinCodeState(code).label })) : codes),
    [codes]
  )
  const gridList = useGridList(rows)
  const usesCode = usesFor !== null && Array.isArray(rows) ? rows.find(r => r.id === usesFor) : null

  const valid = form.roleID && (!form.maxUses || Number(form.maxUses) >= 1)

  return (
    <div className="sw-inline-panel" role="region" aria-label={`Join codes for ${organizationName}`} onKeyDown={handleKeyDown}>
      <div className="sw-subh">
        <h4 className="sw-label m-0">Join codes for {organizationName}</h4>
        {canEdit && !creating &&
          <button type="button" ref={newButton} className="btn btn-sm sw-btn" onClick={openCreate}>New join code</button>}
      </div>
      <p className="sw-hint m-0">
        People signing in through this connection who aren't placed in another organization can enter a code to join {organizationName}. A code only works through this connection.
      </p>

      {issued &&
        <div className="sw-note d-grid gap-2" role="status">
          <strong>Code {issued.verb}. Copy it now: it won't be shown again.</strong>
          <CopyValue id={`${idp}-issued`} label="Join code" value={issued.code} hint="Share it the way you share other sign-in details with your people." />
          <div className="sw-inline-actions">
            <button type="button" className="btn sw-btn" onClick={() => { setIssued(null); if (newButton.current) newButton.current.focus() }}>Done</button>
          </div>
        </div>}

      {creating &&
        <div className="d-grid gap-2" data-testid="sw-code-form">
          <div className="sw-rule-fields">
            <div className="sw-field">
              <label className="sw-label" htmlFor={`${idp}-label`}>Label</label>
              <input id={`${idp}-label`} className="form-control" maxLength={80} value={form.label} autoFocus autoComplete="off"
                placeholder="such as Fall term staff" onChange={e => set({ label: e.target.value })} />
            </div>
            <div className="sw-field">
              <label className="sw-label" htmlFor={`${idp}-role`}>Role</label>
              <select id={`${idp}-role`} className="form-select" value={form.roleID} onChange={e => set({ roleID: e.target.value })} aria-describedby={`${idp}-role-hint`}>
                <option value="" disabled>Choose a role</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <span className="sw-hint" id={`${idp}-role-hint`}>A code can give only the default member role, or a role with no more access than it.</span>
            </div>
            <div className="sw-field">
              <label className="sw-label" htmlFor={`${idp}-expires`}>Expires</label>
              <input id={`${idp}-expires`} type="date" className="form-control" value={form.expires} onChange={e => set({ expires: e.target.value })} aria-describedby={`${idp}-expires-hint`} />
              <span className="sw-hint" id={`${idp}-expires-hint`}>Leave empty for no expiry.</span>
            </div>
            <div className="sw-field">
              <label className="sw-label" htmlFor={`${idp}-max`}>Maximum uses</label>
              <input id={`${idp}-max`} type="number" min="1" step="1" className="form-control" value={form.maxUses} onChange={e => set({ maxUses: e.target.value })} aria-describedby={`${idp}-max-hint`} />
              <span className="sw-hint" id={`${idp}-max-hint`}>Leave empty for no limit.</span>
            </div>
          </div>
          {error && <p className="text-danger small m-0" role="alert">{error}</p>}
          {error && <ErrorReference error={failure} className="mt-0" />}
          <div className="sw-inline-actions">
            <button type="button" className="btn sw-btn" onClick={() => setCreating(false)}>Cancel</button>
            <button type="button" className="btn sw-btn-primary" disabled={!valid || saving} onClick={handleCreate}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>}

      {codes === undefined && <p className="sw-hint m-0">Loading join codes…</p>}
      {codes === null && <p className="sw-hint m-0">Join codes couldn't be loaded.</p>}
      {codes &&
        <FleetGrid tableRef={tableRef} exportName="join-codes" compact flush title="join codes">
          <TableAgClient
            ref={tableRef}
            queryKey="codes"
            apiList={gridList}
            emptyState={{ Icon: IconLoginPin, title: 'No join codes yet', message: canEdit ? `Create one so people can join ${organizationName} with it.` : `Nobody has created a code for ${organizationName}.` }}>
            <ColumnText field="name" header="Code" sortable />
            <ColumnText field="roleName" header="Role" sortable />
            <ColumnFormat field="useCount" header="Uses" render={usesCell} sortable />
            <ColumnFormat field="expiresAt" header="Expires" render={expiresCell} sortable />
            <ColumnFormat field="status" header="Status" render={statusCell} sortable />
            <ColumnMenu
              menu={code =>
                <Menu small>
                  <MenuItem Icon={IconUsers} text="joined by" pin show onClick={() => toggleUses(code)} />
                  <MenuItem Icon={IconReload} text="rotate" pin show={canEdit && !code.revokedAt} onClick={() => { setConfirmError(null); setConfirm({ kind: 'rotate', code }) }} />
                  <MenuItem Icon={IconDelete} text="revoke" pin show={canEdit && !code.revokedAt} onClick={() => { setConfirmError(null); setConfirm({ kind: 'revoke', code }) }} />
                </Menu>}
            />
          </TableAgClient>
        </FleetGrid>}
      {codes && codes.length > 0 && <p className="sw-hint m-0">{plural(codes.reduce((n, c) => n + (c.useCount || 0), 0), 'person has', 'people have')} joined with these codes.</p>}

      {/* Who joined with a code: under the grid, for the code whose "joined
          by" was chosen (a grid row can't hold it). The same action closes it. */}
      {usesCode &&
        <div className="sw-inline-panel" role="region" aria-label={`Who joined with ${usesCode.name}`} data-testid="sw-code-uses">
          <div className="sw-subh">
            <h5 className="sw-label m-0">Joined with {usesCode.name}</h5>
            <button type="button" className="btn btn-sm sw-btn" onClick={() => setUsesFor(null)}>Close</button>
          </div>
          {uses === undefined && <p className="sw-hint m-0">Loading…</p>}
          {uses === null && <p className="sw-hint m-0">Couldn't load who joined.</p>}
          {uses && uses.length === 0 && <p className="sw-hint m-0">Nobody has joined with this code yet.</p>}
          {uses && uses.length > 0 &&
            <ul className="list-unstyled m-0 d-grid gap-1" aria-label={`Joined with ${usesCode.name}`}>
              {uses.map(u =>
                <li key={`${u.loginID}-${u.joinedAt}`}>
                  <span>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</span>
                  {(u.firstName || u.lastName) && <span className="sw-hint"> {u.email}</span>}
                  <span className="sw-hint"> · {formatDateTime(u.joinedAt)}</span>
                </li>)}
            </ul>}
        </div>}

      <ConfirmDialog open={Boolean(confirm)} busy={busy} error={confirmError} failure={confirmFailure}
        title={confirm ? (confirm.kind === 'rotate' ? 'Rotate this code?' : 'Revoke this code?') : ''}
        body={confirm
          ? (confirm.kind === 'rotate'
              ? `The old code stops working now and you get a new one with the same settings. People who already joined stay in ${organizationName}.`
              : `It stops working now. People who already joined stay in ${organizationName}.`)
          : ''}
        confirmText={confirm && confirm.kind === 'rotate' ? 'Rotate' : 'Revoke'}
        onConfirm={handleConfirm} onCancel={() => setConfirm(null)} />
    </div>
  )
}
